'use client';

import { useState } from 'react';
import { useSpellingLocalization } from '@/components/providers/spelling-provider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Example text with different spelling variants
const examples = [
  "The organization specializes in optimizing color quality.",
  "The company's theater center utilizes advanced technology for visualization.",
  "Our neighbors have traveled to the program's defense center.",
  "The dialog focused on catalog items and labor practices.",
  "The center's program fulfillment prioritizes humor and flavor.",
];

export default function SpellingDemoPage() {
  const { localizeText, currentLocale } = useSpellingLocalization();
  const [customText, setCustomText] = useState('');
  const [localizedCustomText, setLocalizedCustomText] = useState('');
  
  const handleTextLocalization = () => {
    setLocalizedCustomText(localizeText(customText));
  };
  
  // Localize variants for display
  const usVariant = currentLocale === 'en' ? '(Current)' : '';
  const auVariant = currentLocale === 'en-AU' ? '(Current)' : '';
  const nzVariant = currentLocale === 'en-NZ' ? '(Current)' : '';
  
  return (
    <div className="container py-16 max-w-4xl">
      <h1 className="text-3xl font-bold mb-8 text-center">Spelling Localization Demo</h1>
      
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Current Locale: <span className="text-primary">{currentLocale}</span></h2>
        <p className="text-muted-foreground mb-4">
          This demo shows how text spelling is automatically localized between US English (en) and 
          UK/Australian/New Zealand English (en-AU, en-NZ). Try switching the language in the navbar.
        </p>
      </div>
      
      <Tabs defaultValue="examples" className="mb-8">
        <TabsList className="mb-4">
          <TabsTrigger value="examples">Examples</TabsTrigger>
          <TabsTrigger value="custom">Try Your Own</TabsTrigger>
        </TabsList>
        
        <TabsContent value="examples">
          <Card>
            <CardHeader>
              <CardTitle>Example Sentences</CardTitle>
              <CardDescription>
                These sentences contain words with different spelling variants between US and UK/AU/NZ English
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {examples.map((example, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-20 font-medium">US {usVariant}:</div>
                      <div className="flex-1 p-2 bg-muted rounded-md">{example}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-20 font-medium">AU/NZ {auVariant || nzVariant}:</div>
                      <div className="flex-1 p-2 bg-muted rounded-md">{localizeText(example)}</div>
                    </div>
                    <hr className="my-4" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="custom">
          <Card>
            <CardHeader>
              <CardTitle>Try Your Own Text</CardTitle>
              <CardDescription>
                Enter text with US English spelling to see it localized to your current locale
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Enter text (US English spelling):</label>
                  <Input
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    placeholder="e.g., The organization specializes in color optimization"
                    className="w-full"
                  />
                </div>
                
                <Button onClick={handleTextLocalization}>Localize Spelling</Button>
                
                {localizedCustomText && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-1">Localized for {currentLocale}:</label>
                    <div className="p-3 bg-muted rounded-md">
                      {localizedCustomText}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="text-sm text-muted-foreground">
              Try words like: color, organization, specialized, theater, neighbor, catalog
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
      
      <div className="bg-primary-foreground p-6 rounded-lg">
        <h3 className="text-lg font-semibold mb-3">How it works</h3>
        <p className="mb-2">
          Our spelling localization system automatically converts between US and UK/AU/NZ English spelling conventions based on the user's selected locale.
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm">
          <li>-ize → -ise (e.g., specialize → specialise)</li>
          <li>-or → -our (e.g., color → colour)</li>
          <li>-er → -re (e.g., center → centre)</li>
          <li>-og → -ogue (e.g., dialog → dialogue)</li>
          <li>And many other spelling differences</li>
        </ul>
      </div>
    </div>
  );
} 